import React, { Fragment, useState } from "react";
import { Card, Drawer, Tag, Tooltip } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import {
  LuBriefcase,
  LuBriefcaseBusiness,
  LuCar,
  LuCircleUser,
  LuExternalLink,
} from "react-icons/lu";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import CustomButton from "../commonComponents/button";
import useCrm from "../../hooks/useCrm";
import {
  getUniqueVehicleOfInterest,
  getVehicleName,
} from "../engagements/activityCenter/engagementHelpers";
import { ROOT_OPP_TYPES } from "../../library/oppTypeRootConfig";

import ReviewInvite from "../../icons/js/reviewInviteIcon";
import MessageEnvalopeOpen from "../../icons/js/MessageEnvalopeOpen";
import SmileyColorIcon from "../../icons/js/SmileyColorIcon";
import SadColorIcon from "../../icons/js/SadColorIcon";
import PencilIcon from "../../icons/js/PencilIcon";
import EnvalopeIcon from "../../icons/js/EnvalopeIcon";
import GoogleIconJsx from "../../icons/js/GoogleIcon";
import FacebookIcon from "../../icons/js/facebookIcon";
import { SVGIcon } from "../../library/basicComponents";
import DealerraterIcon from "../../../static/icons/DealerraterIcon.svg";
import YelpIcon from "../../../static/icons/YelpIcon.svg";
import MessageIcon from "../../../static/icons/MessageIcon.svg";
import CarsDotComIcon from "../../../static/icons/carsdotcom.svg";
import EngagedAILogo from "../../../static/EngagedAi_trans_bg.svg";

import {
  getTrackingStagesFromActions,
  getTrackingStagesFromDetails,
  PLATFORM_LABELS,
  PLATFORMS,
  STAGE,
} from "../../helpers/reviewTrackingHelper";

// Tracking timestamps come from the API as UTC; parse as UTC then convert to the
// dealership's location timezone when provided (see currentLocationTimezone),
// falling back to the viewer's local zone so the timeline reads correctly everywhere.
dayjs.extend(utc);
dayjs.extend(timezone);

const STAGE_LABELS = {
  [STAGE.INVITE_SENT]: "Invite sent",
  [STAGE.INVITE_OPENED]: "Invite opened",
  [STAGE.GOOD]: "Positive experience",
  [STAGE.BAD]: "Negative experience",
  [STAGE.FEEDBACK_SUBMITTED]: "Feedback submitted",
  [STAGE.OTTO_USED]: "Review drafted by Otto",
  [STAGE.SELF_WRITTEN]: "Self written review",
  [STAGE.REVIEW_POSTED]: "Review posted",
};

const STAGE_TOOLTIPS = {
  [STAGE.INVITE_SENT]: "Review invite sent",
  [STAGE.INVITE_OPENED]: "Lead opened the invite",
  [STAGE.GOOD]: "Lead indicated a satisfactory experience",
  [STAGE.BAD]: "Lead indicated an unsatisfactory experience",
  [STAGE.FEEDBACK_SUBMITTED]: "Lead submitted feedback",
  [STAGE.OTTO_USED]: "AI-generated review presented to the customer",
  [STAGE.SELF_WRITTEN]: "Review drafted by the customer",
  [STAGE.REVIEW_POSTED]: "Review posted",
};

const PLATFORM_ICON = {
  [PLATFORMS.GOOGLE]: (size, sizePx) => <GoogleIconJsx width={size} height={size} />,
  [PLATFORMS.FACEBOOK]: (size, sizePx) => <FacebookIcon width={size} height={size} />,
  [PLATFORMS.DEALER_RATER]: (size, sizePx) => <SVGIcon size={sizePx} component={DealerraterIcon} />,
  [PLATFORMS.YELP]: (size, sizePx) => <SVGIcon size={sizePx} component={YelpIcon} />,
  [PLATFORMS.CARS_COM]: (size, sizePx) => <SVGIcon size={sizePx} component={CarsDotComIcon} />,
};

const rawLabel = stage => {
  if (!stage.actionType) return "Activity";
  return stage.actionValue ? `${stage.actionType} ${stage.actionValue}` : stage.actionType;
};

export const labelFor = stage => {
  if (stage.key === STAGE.PLATFORM_CLICKED) {
    return `Opened ${PLATFORM_LABELS[stage.platform] || "review site"}`;
  }
  if (stage.key === STAGE.REVIEW_POSTED && stage.platform) {
    return `Posted on ${PLATFORM_LABELS[stage.platform] || "review site"}`;
  }
  if (stage.key === STAGE.UNKNOWN) return rawLabel(stage);
  return STAGE_LABELS[stage.key] || rawLabel(stage);
};

const tooltipFor = stage => {
  if (stage.key === STAGE.PLATFORM_CLICKED) {
    return `Customer opened ${PLATFORM_LABELS[stage.platform] || "review site"}`;
  }
  if (stage.key === STAGE.REVIEW_POSTED && stage.platform) {
    return `Customer posted a review on ${PLATFORM_LABELS[stage.platform] || "review site"}`;
  }
  if (stage.key === STAGE.UNKNOWN) return rawLabel(stage);
  return STAGE_TOOLTIPS[stage.key] || rawLabel(stage);
};

// Short tooltip label for the "Feedback submitted" stage — shared by
// ReviewTrackingInline and ReviewTrackingTimeline so both timeline
// surfaces read the same way instead of dumping the full review text.
export const feedbackTooltipLabel = reviewType =>
  reviewType === "GOOD" ? "Positive feedback received" : "Negative feedback received";

// Tooltip body for a stage. For "Feedback submitted", callers that pass
// `feedbackLabel` (the timeline surfaces) get that short label; callers
// that don't (e.g. the table's icon-only strip) keep the legacy
// full-comment tooltip via `negativeComment`. No tooltip when neither is
// present. All other stages keep their standard descriptive tooltip.
const tooltipContentFor = (stage, negativeComment, feedbackLabel) => {
  if (stage.key === STAGE.FEEDBACK_SUBMITTED) {
    if (feedbackLabel) return feedbackLabel;
    const comment = (negativeComment || "").trim();
    return comment ? (
      <div className="whitespace-pre-line break-words max-w-[280px]">{comment}</div>
    ) : null;
  }
  return tooltipFor(stage);
};

// Wraps children in a Tooltip only when there's content to show, so the
// feedback-submitted icon renders with no tooltip when there's no comment.
const StageTooltip = ({ stage, negativeComment, feedbackLabel, children }) => {
  const content = tooltipContentFor(stage, negativeComment, feedbackLabel);
  if (!content) return children;
  return <Tooltip title={content}>{children}</Tooltip>;
};

export const StageIcon = ({ stage, size }) => {
  const sizePx = `${size}px`;
  const platformIcon = PLATFORM_ICON[stage.platform];
  switch (stage.key) {
    case STAGE.INVITE_SENT:
      return <ReviewInvite width={size} height={size} />;
    case STAGE.INVITE_OPENED:
      return <MessageEnvalopeOpen width={size} height={size} />;
    case STAGE.GOOD:
      return <SmileyColorIcon width={size} height={size} />;
    case STAGE.BAD:
      return <SadColorIcon width={size} height={size} />;
    case STAGE.FEEDBACK_SUBMITTED:
      return <SVGIcon size={sizePx} component={MessageIcon} />;
    case STAGE.OTTO_USED:
      return <SVGIcon size={sizePx} component={EngagedAILogo} />;
    case STAGE.SELF_WRITTEN:
      return <PencilIcon width={size} height={size} />;
    case STAGE.REVIEW_POSTED:
      return platformIcon ? (
        platformIcon(size, sizePx)
      ) : (
        <EnvalopeIcon width={size} height={size} />
      );
    case STAGE.PLATFORM_CLICKED:
      return platformIcon ? platformIcon(size, sizePx) : <UnknownDot size={size} />;
    default:
      return <UnknownDot size={size} />;
  }
};

const UnknownDot = ({ size }) => (
  <span
    style={{
      display: "inline-block",
      width: size,
      height: size,
      borderRadius: "50%",
      background: "#9CA3AF",
    }}
  />
);

export const stageKey = stage =>
  stage.uid || (stage.platform ? `${stage.key}_${stage.platform}` : stage.key);

export const toStages = data => {
  if (!data) return [];
  if (Array.isArray(data)) {
    if (data.length === 0) return [];
    return typeof data[0] === "string"
      ? getTrackingStagesFromActions(data)
      : getTrackingStagesFromDetails(data);
  }
  if (typeof data === "object") {
    const fromTrackings = getTrackingStagesFromDetails(data.trackings);
    if (fromTrackings.length > 0) return fromTrackings;
    return getTrackingStagesFromActions(data.actions);
  }
  return [];
};

// A GOOD review response has no "SUBMIT|BAD" tracking action (that only
// fires for negative feedback), so append a synthetic "Feedback submitted"
// stage when it's otherwise missing — shared by ReviewTrackingInline and
// ReviewTrackingTimeline so both surfaces stay in sync.
export const withGoodFeedbackStage = (stages, reviewType, timestamp = null) => {
  if (reviewType !== "GOOD" || stages.some(stage => stage.key === STAGE.FEEDBACK_SUBMITTED)) {
    return stages;
  }
  return [...stages, { uid: "feedback_submitted_good", key: STAGE.FEEDBACK_SUBMITTED, timestamp }];
};

// Shared "View Feedback" drawer used by both the activity center inline
// timeline (ReviewTrackingInline) and the Review Analytics timeline
// (ReviewTrackingTimeline) so the negative-feedback detail view is identical
// in both places.
export const FeedbackViewDrawer = ({
  open,
  onClose,
  leadName,
  timestamp,
  comment,
  currentLocationTimezone,
  opportunityLabel,
  opportunityHref,
  assignedUsers = [],
  vehicleLabel,
  reviewType,
  rootOppTypeName,
}) => {
  const isPositive = reviewType === "GOOD";
  const feedbackLabel = isPositive ? "Positive Experience" : "Negative Experience";

  const rootOppTypeConfig = rootOppTypeName
    ? ROOT_OPP_TYPES.find(
        cfg => cfg.displayName.toLowerCase() === rootOppTypeName.toLowerCase()
      ) || {
        Icon: LuBriefcaseBusiness,
        displayName: rootOppTypeName,
        chipBg: "rgba(0, 0, 0, 0.06)",
        stroke: "rgba(0, 0, 0, 0.65)",
      }
    : null;
  const formattedTimestamp = timestamp
    ? (currentLocationTimezone
        ? dayjs.utc(timestamp).tz(currentLocationTimezone)
        : dayjs.utc(timestamp).local()
      ).format("MMM D, YYYY, h:mm A")
    : "";

  return (
    <Drawer title="View Feedback" open={open} onClose={onClose} width={600} closable>
      <div className="flex flex-col gap-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-black-65">LEAD INFO</span>
            {/* Opp-type root badge (Sales / Service / Parts / ...), styled per
                the matching entry in oppTypeRootConfig.js. */}
            {rootOppTypeConfig && (
              <span
                className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
                style={{ background: rootOppTypeConfig.chipBg, color: rootOppTypeConfig.stroke }}
              >
                <rootOppTypeConfig.Icon size={14} />
                {rootOppTypeConfig.displayName}
              </span>
            )}
          </div>
          <Card size="small" bodyStyle={{ padding: 12 }}>
            <div className="flex items-center justify-between">
              <span className="font-bold text-base text-black-88">{leadName || "Unknown"}</span>
              {formattedTimestamp && (
                <span className="text-xs font-normal text-black-65">{formattedTimestamp}</span>
              )}
            </div>
            <div className="flex flex-col gap-2 mt-2">
              {opportunityLabel && (
                <div className="flex items-center gap-2">
                  <LuBriefcase className="icon-color" size={14} />
                  {opportunityHref ? (
                    <CustomButton
                      type="link"
                      btnText={opportunityLabel}
                      className="p-0 h-auto text-xs"
                      handleClick={() =>
                        window.open(opportunityHref, "_blank", "noopener,noreferrer")
                      }
                    />
                  ) : (
                    <span className="text-black-65 text-xs">{opportunityLabel}</span>
                  )}
                </div>
              )}
              {assignedUsers.length > 0 && (
                <div className="flex items-start gap-2">
                  <LuCircleUser className="icon-color mt-0.5" size={14} />
                  <div className="flex flex-col">
                    {assignedUsers.map((label, i) => (
                      <span key={`assigned-user-${i}`} className="text-black-65 text-xs">
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {vehicleLabel && (
                <div className="flex items-center gap-2">
                  <LuCar className="icon-color" size={14} />
                  <span className="text-black-65 text-xs">{vehicleLabel}</span>
                </div>
              )}
            </div>
          </Card>
        </div>
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-black-65 mb-2">FEEDBACK SUBMITTED</span>
            <div
              className="text-xs font-medium px-2 py-1 rounded-[4px]"
              style={
                isPositive
                  ? { background: "#F6FFED", color: "#389E0D" }
                  : { background: "#FFF1F0", color: "#CF1322" }
              }
            >
              {feedbackLabel}
            </div>
          </div>
          {/* A response can be submitted without any text (a GOOD rating in
              particular), so say so rather than showing an empty box. */}
          <div className="bg-[#F5F5F5] rounded-md p-3 text-sm text-black-88 whitespace-pre-line break-words">
            {(comment || "").trim() || (
              <span className="italic text-black-65">No comment provided</span>
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export const ReviewTrackingInline = ({
  data,
  negativeComment,
  leadName,
  currentLocationTimezone,
  opportunityLabel,
  opportunityHref,
  assignedUsers,
  vehicleLabel,
  reviewType,
  rootOppTypeName,
  timestamp,
}) => {
  const [viewingStage, setViewingStage] = useState(null);
  const stages = withGoodFeedbackStage(toStages(data), reviewType);
  if (stages.length === 0) return null;

  // The drawer is offered whenever a review response exists, comment or not —
  // it still carries the lead/opportunity context and the positive/negative
  // outcome, and FeedbackViewDrawer shows a placeholder in place of the text.
  const hasReviewResponse = Boolean(reviewType);

  return (
    <div className="w-[95%] h-full flex flex-col justify-center items-center mx-auto mt-2 max-w-[800px]">
      <div className="flex flex-row flex-wrap w-full items-center justify-center gap-3 mb-2">
        {stages.map((stage, i) => (
          <Fragment key={stageKey(stage)}>
            {i > 0 && <ArrowRightOutlined />}
            <StageTooltip
              stage={stage}
              negativeComment={negativeComment}
              feedbackLabel={
                stage.key === STAGE.FEEDBACK_SUBMITTED ? feedbackTooltipLabel(reviewType) : null
              }
            >
              <span className="flex flex-row gap-1 items-center text-xs text-grey-700">
                <StageIcon stage={stage} size={14} />
                {labelFor(stage)}
                {stage.key === STAGE.FEEDBACK_SUBMITTED && hasReviewResponse && (
                  <LuExternalLink
                    className="cursor-pointer text-twLinkColor"
                    size={14}
                    onClick={() => setViewingStage(stage)}
                  />
                )}
              </span>
            </StageTooltip>
          </Fragment>
        ))}
      </div>
      <FeedbackViewDrawer
        open={!!viewingStage}
        onClose={() => setViewingStage(null)}
        leadName={leadName}
        timestamp={timestamp || viewingStage?.timestamp}
        comment={negativeComment}
        currentLocationTimezone={currentLocationTimezone}
        opportunityLabel={opportunityLabel}
        opportunityHref={opportunityHref}
        assignedUsers={assignedUsers}
        vehicleLabel={vehicleLabel}
        reviewType={reviewType}
        rootOppTypeName={rootOppTypeName}
      />
    </div>
  );
};

export const ReviewTrackingIcons = ({ data, negativeComment }) => {
  const stages = toStages(data);
  if (stages.length === 0) return null;

  return (
    <div className="flex flex-row flex-wrap gap-1 items-center">
      {stages.map(stage => (
        <StageTooltip key={stageKey(stage)} stage={stage} negativeComment={negativeComment}>
          <span className="inline-flex items-center">
            <StageIcon stage={stage} size={14} />
          </span>
        </StageTooltip>
      ))}
    </div>
  );
};

export const ReviewTrackingTimeline = ({ record, currentLocationTimezone }) => {
  const { getOpprDetails } = useCrm();
  const [viewingStage, setViewingStage] = useState(null);
  const stages = toStages(record?.trackings);
  const reviewType = record?.reviewResponse?.responseType;

  const withTimestamps = stages.map(stage => {
    if (stage.key === STAGE.INVITE_SENT && !stage.timestamp) {
      return { ...stage, timestamp: record?.invitedDate || record?.createTs || null };
    }
    if (stage.key === STAGE.FEEDBACK_SUBMITTED && !stage.timestamp) {
      return { ...stage, timestamp: record?.reviewResponse?.commentTs || null };
    }
    return stage;
  });
  const enriched = withGoodFeedbackStage(
    withTimestamps,
    reviewType,
    record?.reviewResponse?.commentTs || null
  );

  if (enriched.length === 0) {
    return <div className="py-6 px-4 bg-[#FAFAFA] text-[#6B7280] text-xs">No activity yet.</div>;
  }

  const userName = record?.user
    ? `${record.user.firstName || ""} ${record.user.lastName || ""}`.trim()
    : "";
  const userCompanyRole = record?.user?.companyRole?.name || "";
  const leadName = record?.lead?.fullName || "";

  // Lead's free-text feedback response — shown on the "feedback submitted"
  // step's tooltip and in the View Feedback drawer when present.
  const negativeComment = record?.reviewResponse?.comment;

  // Lead Info panel (opportunity, assigned user, vehicle) reads from
  // reviewResponse.user / reviewResponse.leadOpportunity — the BE nests
  // these under the review-response entity rather than on the tracking
  // record itself.
  const leadOpportunity = record?.reviewResponse?.leadOpportunity;
  const { label: opportunityLabel, crmLink: opportunityHref } = leadOpportunity
    ? getOpprDetails({ opportunity: leadOpportunity, crmLeadObj: record?.lead?.crmLeadObj })
    : {};

  const reviewResponseUser = record?.reviewResponse?.user;
  const reviewResponseUserName = reviewResponseUser
    ? `${reviewResponseUser.firstName || ""} ${reviewResponseUser.lastName || ""}`.trim()
    : "";
  const reviewResponseUserCompanyRole = reviewResponseUser?.companyRole?.name || "";
  const assignedUsers = reviewResponseUserName
    ? [
        reviewResponseUserCompanyRole
          ? `${reviewResponseUserName} (${reviewResponseUserCompanyRole})`
          : reviewResponseUserName,
      ]
    : [];

  // Prefer the vehicle flagged isPrimary; fall back to the first one when
  // none is marked primary.
  const vehicles = getUniqueVehicleOfInterest(leadOpportunity?.vehiclesOfInterest);
  const primaryVehicle = vehicles?.find(v => v.isPrimary) || vehicles?.[0];
  const vehicleLabel = getVehicleName(primaryVehicle);

  // Root opp-type (Sales / Service / Parts) badge — parentType is the root;
  // when opportunityType has no parent it IS the root itself.
  const rootOppTypeName =
    leadOpportunity?.opportunityType?.parentType?.name || leadOpportunity?.opportunityType?.name;

  return (
    <div className="py-6 px-5 bg-white overflow-x-auto">
      <div className="flex items-start gap-3 min-w-max">
        {enriched.map((stage, index) => {
          const label = labelFor(stage);
          // "View" is offered on the feedback step whenever a review response
          // exists, comment or not — the drawer still carries the lead /
          // opportunity context and the outcome, and shows a placeholder in
          // place of the text.
          const canViewFeedback = stage.key === STAGE.FEEDBACK_SUBMITTED && Boolean(reviewType);
          // Feedback-submitted step shows a short "Negative/Positive feedback
          // received" tooltip (see FeedbackViewDrawer for the full text);
          // every other step keeps its standard descriptive label.
          const tooltipTitle =
            stage.key === STAGE.FEEDBACK_SUBMITTED ? feedbackTooltipLabel(reviewType) : label;
          return (
            <div key={stageKey(stage)} className="flex items-start gap-3">
              <div className="flex flex-col items-center text-center w-[128px] shrink-0">
                <Tooltip title={tooltipTitle}>
                  <div className="w-10 h-10 rounded-full bg-green-50 ring-2 ring-green-100 flex items-center justify-center transition-shadow hover:shadow-sm">
                    <StageIcon stage={stage} size={20} />
                  </div>
                </Tooltip>
                <span className="mt-2 text-xs font-semibold text-black-88 line-clamp-2">
                  {label}
                </span>
                {stage.timestamp && (
                  <span className="mt-0.5 text-[11px] text-gray-400">
                    {(currentLocationTimezone
                      ? dayjs.utc(stage.timestamp).tz(currentLocationTimezone)
                      : dayjs.utc(stage.timestamp).local()
                    ).format("MMM D, YYYY, h:mm A")}
                  </span>
                )}
                {stage.key === STAGE.INVITE_SENT && userName && (
                  <span className="text-[11px] text-gray-400">
                    Sent by {userName}
                    {userCompanyRole && ` (${userCompanyRole})`}
                  </span>
                )}
                {canViewFeedback && (
                  <CustomButton
                    type="link"
                    btnText="View"
                    className="p-0 h-auto mt-1 text-xs"
                    handleClick={() => setViewingStage(stage)}
                  />
                )}
              </div>
              {index < enriched.length - 1 && (
                <div className="flex items-center pt-[18px] shrink-0">
                  <div className="h-[3px] w-12 bg-gradient-to-r from-green-300 to-green-200 rounded-full" />
                </div>
              )}
            </div>
          );
        })}
      </div>
      <FeedbackViewDrawer
        open={!!viewingStage}
        onClose={() => setViewingStage(null)}
        leadName={leadName}
        timestamp={viewingStage?.timestamp}
        comment={negativeComment}
        currentLocationTimezone={currentLocationTimezone}
        opportunityLabel={opportunityLabel}
        opportunityHref={opportunityHref}
        assignedUsers={assignedUsers}
        vehicleLabel={vehicleLabel}
        reviewType={reviewType}
        rootOppTypeName={rootOppTypeName}
      />
    </div>
  );
};
